import React, { useState, useEffect } from 'react';
import { 
  ChevronDown, 
  CheckCircle2,
  Circle,
  Lock,
  Target,
  ArrowRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { PROGRAMME_STAGES } from '../../data/stageData';

// Stage outcomes - customer-friendly descriptions
const STAGE_OUTCOMES = {
  stage00: {
    title: "Entrepreneurial Capability Assessment",
    description: "Discover if entrepreneurship is right for you through guided conversations and self-reflection.",
    outcomes: [
      "Understand your entrepreneurial readiness",
      "Identify your strengths and areas to develop",
      "Get a personalised path recommendation"
    ]
  },
  stage0: {
    title: "Idea Generation & Discovery",
    description: "Explore possibilities and find a business idea that matches your skills and interests.",
    outcomes: [
      "Clarify what kind of business suits you",
      "Generate and evaluate business ideas",
      "Choose your best idea to pursue"
    ]
  },
  stage1: {
    title: "Validation & Foundation",
    description: "Test your idea with real customers and build the foundation for your business.",
    outcomes: [
      "Talk to potential customers",
      "Validate your business idea",
      "Create your business model"
    ]
  },
  stage2: {
    title: "Business Setup & Legal",
    description: "Set up your business properly with the right structure, registrations, and finances.",
    outcomes: [
      "Register your business",
      "Set up banking and accounting",
      "Understand your financial needs"
    ]
  },
  stage3: {
    title: "Tech Foundation & MVP",
    description: "Build the minimum version of your product to start getting real customers.",
    outcomes: [
      "Define your MVP features",
      "Build or source your first product",
      "Get ready for launch"
    ]
  },
  stage4: {
    title: "Capital & Fundraising",
    description: "Secure the funding you need to grow your business.",
    outcomes: [
      "Understand your funding options",
      "Perfect your pitch",
      "Connect with investors"
    ]
  },
  stage5: {
    title: "Mentorship & Advisory Board",
    description: "Build a support network of experienced advisors to guide your growth.",
    outcomes: [
      "Identify the expertise you need",
      "Recruit advisors",
      "Establish regular guidance sessions"
    ]
  },
  stage6: {
    title: "Growth & Brand",
    description: "Launch your marketing and start acquiring customers at scale.",
    outcomes: [
      "Define your brand positioning",
      "Launch marketing campaigns",
      "Build your customer base"
    ]
  },
  stage7: {
    title: "Scaling Operations",
    description: "Build the team and systems to scale your business sustainably.",
    outcomes: [
      "Hire your first team members",
      "Document your processes",
      "Scale your operations"
    ]
  }
};

const ProgrammeDetails = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedStage, setExpandedStage] = useState(null);

  useEffect(() => {
    fetchStages();
  }, []);

  const fetchStages = async () => {
    try {
      const response = await axios.get(`${API}/programme/stages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStages(response.data.stages);
    } catch (error) {
      console.error('Error fetching stages:', error);
    }
    setLoading(false);
  };

  const getStageStatus = (stageId) => {
    const stage = stages.find(s => s.id === stageId);
    return stage?.status || 'locked';
  };

  const completedCount = stages.filter(s => s.status === 'completed').length;
  const progressPercent = Math.round((completedCount / PROGRAMME_STAGES.length) * 100);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-black/40">Loading programme details...</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl headline text-black mb-3">The SPRING Programme</h1>
        <p className="text-black/60 body-text max-w-2xl mx-auto">
          Your complete journey from first idea to thriving business. 
          Every step is mapped out — you always know what comes next.
        </p>
      </div>

      {/* Progress Overview */}
      <div className="bg-white rounded-none p-6 border border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="subhead text-black">Your Progress</h2>
            <p className="text-sm text-black/50 mt-1">
              {completedCount} of {PROGRAMME_STAGES.length} stages completed
            </p>
          </div>
          <span className="text-3xl headline text-black">{progressPercent}%</span>
        </div>
        <div className="h-3 bg-secondary rounded-none overflow-hidden">
          <div 
            className="h-full bg-primary rounded-none transition-all"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Stages List - Customer Friendly */}
      <div className="space-y-3">
        {PROGRAMME_STAGES.map((stage) => {
          const status = getStageStatus(stage.id);
          const isExpanded = expandedStage === stage.id;
          const stageInfo = STAGE_OUTCOMES[stage.id] || {};
          const isCompleted = status === 'completed';
          const isInProgress = status === 'in_progress';
          const isLocked = status === 'locked';
          
          return (
            <div
              key={stage.id}
              className={`bg-white rounded-none border overflow-hidden transition-all ${
                isInProgress ? 'border-primary ' : 
                isCompleted ? 'border-green-200' : 'border-border'
              }`}
            >
              {/* Stage Header */}
              <button
                onClick={() => setExpandedStage(isExpanded ? null : stage.id)}
                className="w-full p-5 flex items-center gap-4 text-left hover:bg-secondary/30 transition-colors"
              >
                <div className={`w-12 h-12 rounded-none flex items-center justify-center flex-shrink-0 ${
                  isCompleted ? 'bg-[rgba(255,192,0,0.15)]' :
                  isInProgress ? 'bg-primary' :
                  'bg-secondary'
                }`}>
                  {isCompleted ? (
                    <CheckCircle2 className="h-6 w-6 text-[#1E491A]" />
                  ) : isInProgress ? (
                    <span className="text-lg font-bold text-white">{stage.number}</span>
                  ) : (
                    <Circle className="h-5 w-5 text-black/30" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-black/40 font-medium">STAGE {stage.number}</span>
                    {isCompleted && (
                      <span className="text-xs bg-[rgba(255,192,0,0.15)] text-[#1E491A] px-2 py-0.5 rounded-none">Done</span>
                    )}
                    {isInProgress && (
                      <span className="text-xs bg-primary text-white px-2 py-0.5 rounded-none">Current</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-black">{stage.name}</h3>
                </div>
                <ChevronDown className={`h-5 w-5 text-black/40 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
              </button>

              {/* Expanded Content - Customer Friendly */}
              {isExpanded && (
                <div className="px-5 pb-5 border-t border-border">
                  <div className="pt-5">
                    {/* Description */}
                    <p className="text-black/70 mb-6">
                      {stageInfo.description || stage.summary}
                    </p>

                    {/* What you'll achieve */}
                    {stageInfo.outcomes && (
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-black mb-3 flex items-center gap-2">
                          <Target className="h-4 w-4 text-primary" />
                          What you'll achieve
                        </h4>
                        <ul className="space-y-2">
                          {stageInfo.outcomes.map((outcome, idx) => (
                            <li key={idx} className="flex items-start gap-3 text-sm text-black/70">
                              <CheckCircle2 className="h-4 w-4 text-[#F6ADBC] flex-shrink-0 mt-0.5" />
                              {outcome}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Action Button */}
                    {isInProgress && (
                      <Button
                        onClick={() => navigate('/portal/stages')}
                        className="bg-primary text-white hover:bg-primary/90 rounded-none"
                      >
                        Go to My Journey
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    )}

                    {isCompleted && (
                      <div className="flex items-center gap-2 text-sm text-[#1E491A]">
                        <CheckCircle2 className="h-4 w-4" />
                        Stage completed
                      </div>
                    )}

                    {isLocked && (
                      <p className="text-sm text-black/40">
                        Complete the previous stages to unlock this one.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Help Section */}
      <div className="bg-secondary/50 rounded-none p-6">
        <h3 className="subhead text-black mb-2">Questions about the programme?</h3>
        <p className="text-sm text-black/60">
          Your coach Brian can explain any stage in detail and help you understand what's coming next.
        </p>
      </div>
    </div>
  );
};

export default ProgrammeDetails;
